import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  saveNutritionEntry,
  getNutritionForDate,
  deleteNutritionEntry,
  updateNutritionEntry,
  getSavedFoodItems,
  saveFoodItem,
  deleteFoodItem,
  estimateMealNutrition
} from '../utils/nutritionManager';
import { getUserWorkoutPlans } from '../utils/userDataManager';
import BarcodeScanner from '../components/BarcodeScanner';
import FoodSearchAutocomplete from '../components/FoodSearchAutocomplete';
import './NutritionTracker.css';

function NutritionTracker() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [todaysGoals, setTodaysGoals] = useState({ calorieGoal: 0, proteinGoal: 0 });
  const [entries, setEntries] = useState([]);
  const [showCalorieInput, setShowCalorieInput] = useState(false);
  const [calorieInput, setCalorieInput] = useState('');
  const [proteinInput, setProteinInput] = useState('');
  const [foodDescription, setFoodDescription] = useState('');
  const [servingSize, setServingSize] = useState('');
  const [servings, setServings] = useState('1');
  const [saveToCommon, setSaveToCommon] = useState(false);
  const [savedFoodItems, setSavedFoodItems] = useState([]);
  const [showSavedItems, setShowSavedItems] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [aiMealDescription, setAiMealDescription] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [complexMealItems, setComplexMealItems] = useState([]);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [showFoodSearch, setShowFoodSearch] = useState(false);
  const [currentFoodData, setCurrentFoodData] = useState(null); // Store API food data for saving
  const [showAiEstimator, setShowAiEstimator] = useState(false); // Collapsed by default

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    loadData();
    loadSavedItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const loadSavedItems = async () => {
    try {
      const items = await getSavedFoodItems();
      setSavedFoodItems(items);
    } catch (err) {
      console.error('Error loading saved items:', err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Load today's nutrition goals from workout plan
      const dayName = new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
      const workoutPlan = await getUserWorkoutPlans();

      if (workoutPlan && workoutPlan[dayName]) {
        setTodaysGoals({
          calorieGoal: workoutPlan[dayName].calorieGoal || 2200,
          proteinGoal: workoutPlan[dayName].proteinGoal || 150
        });
      } else {
        // Default goals if no workout plan
        setTodaysGoals({
          calorieGoal: 2200,
          proteinGoal: 150
        });
      }

      // Load existing entries for selected date
      const existingEntries = await getNutritionForDate(selectedDate);
      setEntries(Array.isArray(existingEntries) ? existingEntries : []);

    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEntry = async () => {
    const caloriesPerServing = parseInt(calorieInput) || 0;
    const proteinPerServing = parseInt(proteinInput) || 0;
    const numServings = parseFloat(servings) || 1;

    if (caloriesPerServing <= 0 && proteinPerServing <= 0) {
      alert('Please enter calories and/or protein');
      return;
    }

    try {
      // Calculate total based on servings
      const totalCalories = Math.round(caloriesPerServing * numServings);
      const totalProtein = Math.round(proteinPerServing * numServings);

      let mealName = foodDescription;
      if (!mealName) {
        const parts = [];
        if (totalCalories > 0) parts.push(`${totalCalories} cal`);
        if (totalProtein > 0) parts.push(`${totalProtein}g protein`);
        mealName = parts.join(', ');
      } else if (numServings !== 1) {
        mealName = `${foodDescription} (${numServings}x)`;
      }

      const result = await saveNutritionEntry({
        date: selectedDate,
        calories: totalCalories,
        protein: totalProtein,
        mealName: mealName
      });

      // Save to common items if checkbox is checked (saves per-serving values)
      if (saveToCommon && foodDescription) {
        const foodItemToSave = {
          name: foodDescription,
          calories: caloriesPerServing,
          protein: proteinPerServing,
          servingUnit: servingSize || 'serving',
          servingQty: 1,
          source: 'manual'
        };

        // If this food came from API/barcode, include rich data
        if (currentFoodData) {
          foodItemToSave.brandName = currentFoodData.brand_name || null;
          foodItemToSave.servingQty = currentFoodData.serving_qty || 1;
          foodItemToSave.servingUnit = currentFoodData.serving_unit || 'serving';
          foodItemToSave.servingWeightGrams = currentFoodData.serving_weight_grams || null;
          foodItemToSave.totalFat = Math.round(currentFoodData.nf_total_fat || 0);
          foodItemToSave.totalCarbohydrate = Math.round(currentFoodData.nf_total_carbohydrate || 0);
          foodItemToSave.sugars = Math.round(currentFoodData.nf_sugars || 0);
          foodItemToSave.dietaryFiber = Math.round(currentFoodData.nf_dietary_fiber || 0);
          foodItemToSave.photoUrl = currentFoodData.photo?.thumb || null;
          foodItemToSave.source = currentFoodData.upc ? 'barcode' : 'search';
          foodItemToSave.nixItemId = currentFoodData.nix_item_id || null;
          foodItemToSave.upc = currentFoodData.upc || null;
        }

        await saveFoodItem(foodItemToSave);
        await loadSavedItems();
      }

      if (result.success) {
        // Warn user if data only saved locally
        if (result.localOnly) {
          alert(`⚠️ WARNING: Data saved offline only!\n\nYour entry was saved to this device but could NOT sync to the cloud.\n\nReason: ${result.supabaseError || result.error || 'Unknown error'}\n\nThis data will NOT appear on your other devices. Check your internet connection and browser console for details.`);
        }

        setCalorieInput('');
        setProteinInput('');
        setFoodDescription('');
        setServingSize('');
        setServings('1');
        setSaveToCommon(false);
        setShowCalorieInput(false);
        setCurrentFoodData(null);
        loadData();
      }
    } catch (err) {
      console.error('Error saving entry:', err);
    }
  };

  const handleSelectSavedItem = (item) => {
    setCalorieInput(item.calories.toString());
    setProteinInput(item.protein.toString());
    setFoodDescription(item.food_name || item.name);
    setServingSize(item.serving_size || item.serving_unit || '');
    setServings('1');
    setCurrentFoodData(null); // Clear API data when selecting saved item
    setShowSavedItems(false);
    setShowCalorieInput(true);
  };

  const handleDeleteSavedItem = async (itemId) => {
    if (!window.confirm('Delete this saved food item?')) return;
    try {
      await deleteFoodItem(itemId);
      await loadSavedItems();
    } catch (err) {
      console.error('Error deleting saved item:', err);
    }
  };

  const handleDeleteEntry = async (entry) => {
    if (!window.confirm('Delete this entry?')) return;

    try {
      if (entry.id) {
        await deleteNutritionEntry(entry.id);
      }
      loadData();
    } catch (err) {
      console.error('Error deleting entry:', err);
    }
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setFoodDescription(entry.food_description || entry.meal_name || '');
    setCalorieInput(entry.calories?.toString() || '');
    setProteinInput(entry.protein?.toString() || '');
    setServings('1');
    setServingSize(entry.serving_size || '');
    setCurrentFoodData(null); // Clear API data when editing
    setShowCalorieInput(true);
    setShowSavedItems(false);
  };

  const handleUpdateEntry = async () => {
    if (!editingEntry) return;

    const calories = parseInt(calorieInput) || 0;
    const protein = parseInt(proteinInput) || 0;

    if (calories <= 0 && protein <= 0) {
      alert('Please enter calories and/or protein');
      return;
    }

    try {
      const result = await updateNutritionEntry(editingEntry.id, {
        calories: calories,
        protein: protein,
        food_description: foodDescription || `${calories} cal, ${protein}g protein`,
        serving_size: servingSize || null
      });

      if (result.success) {
        setEditingEntry(null);
        setCalorieInput('');
        setProteinInput('');
        setFoodDescription('');
        setServingSize('');
        setServings('1');
        setShowCalorieInput(false);
        loadData();
      }
    } catch (err) {
      console.error('Error updating entry:', err);
    }
  };

  const handleAiMealEstimate = async () => {
    if (!aiMealDescription.trim()) {
      alert('Please describe your meal');
      return;
    }

    setAiLoading(true);
    try {
      const result = await estimateMealNutrition(aiMealDescription);

      if (result.success) {
        if (result.isComplex) {
          // Complex meal - show breakdown with servings for each item
          setComplexMealItems(result.items.map(item => ({
            ...item,
            servings: 1 // Default to 1 serving for each item
          })));
          setAiMealDescription('');
          setShowSavedItems(false);
          alert(`Complex Meal Detected!\n\nYour meal has been split into ${result.items.length} separate items:\n${result.items.map(item => `• ${item.mealName} (${item.servingSize})`).join('\n')}\n\nYou can adjust servings for each item individually below.`);
        } else {
          // Simple meal - populate form as before
          setFoodDescription(result.mealName);
          setCalorieInput(result.calories.toString());
          setProteinInput(result.protein.toString());
          setServings('1');
          setServingSize(result.servingSize || '1 serving');
          setAiMealDescription('');
          setShowSavedItems(false);
          alert(`AI Estimation Complete!\n\n${result.mealName}\nPer Serving: ${result.servingSize}\n${result.calories} calories, ${result.protein}g protein\n\nAdjust the "Number of Servings" if needed, then click "Add Entry"`);
        }
      } else {
        alert(result.error || 'Failed to estimate meal nutrition. Please try manual entry.');
      }
    } catch (err) {
      console.error('AI estimation error:', err);
      alert('Failed to estimate meal nutrition. Please try manual entry.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleComplexMealServingsChange = (index, newServings) => {
    const updated = [...complexMealItems];
    updated[index].servings = parseFloat(newServings) || 1;
    setComplexMealItems(updated);
  };

  const handleAddAllComplexItems = async () => {
    let successCount = 0;
    let failureCount = 0;

    for (const item of complexMealItems) {
      const totalCalories = Math.round(item.calories * item.servings);
      const totalProtein = Math.round(item.protein * item.servings);

      let mealName = item.mealName;
      if (item.servings !== 1) {
        mealName = `${item.mealName} (${item.servings}x)`;
      }

      try {
        const result = await saveNutritionEntry({
          date: selectedDate,
          calories: totalCalories,
          protein: totalProtein,
          mealName: mealName
        });

        if (result.success) {
          successCount++;
        } else {
          failureCount++;
        }
      } catch (err) {
        console.error('Error saving complex meal item:', err);
        failureCount++;
      }
    }

    if (successCount > 0) {
      setComplexMealItems([]);
      loadData();

      if (failureCount > 0) {
        alert(`Added ${successCount} items successfully, but ${failureCount} failed to save.`);
      } else {
        alert(`All ${successCount} items added successfully!`);
      }
    } else {
      alert('Failed to add items. Please try again.');
    }
  };

  const handleRemoveComplexItem = (index) => {
    const updated = complexMealItems.filter((_, i) => i !== index);
    setComplexMealItems(updated);
  };

  const handleFoodFound = (foodData) => {
    // Handle food data from barcode scanner or search
    const calories = Math.round(foodData.nf_calories || 0);
    const protein = Math.round(foodData.nf_protein || 0);
    const foodName = foodData.brand_name
      ? `${foodData.brand_name} - ${foodData.food_name}`
      : foodData.food_name;
    const servingInfo = `${foodData.serving_qty} ${foodData.serving_unit}`;

    setFoodDescription(foodName);
    setCalorieInput(calories.toString());
    setProteinInput(protein.toString());
    setServingSize(servingInfo);
    setServings('1');
    setCurrentFoodData(foodData); // Store API data for saving to common items
    setSaveToCommon(true); // Auto-check the save to common checkbox
    setShowCalorieInput(true);
    setShowBarcodeScanner(false);
    setShowFoodSearch(false);
  };

  const handleDateChange = (direction) => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() + direction);
    setSelectedDate(currentDate.toISOString().split('T')[0]);
  };

  // Calculate totals
  const totalCalories = entries.reduce((sum, entry) => sum + (entry.calories || 0), 0);
  const totalProtein = entries.reduce((sum, entry) => sum + (entry.protein || 0), 0);

  // Calculate percentages
  const caloriePercent = todaysGoals.calorieGoal > 0 ? (totalCalories / todaysGoals.calorieGoal) * 100 : 0;
  const proteinPercent = todaysGoals.proteinGoal > 0 ? (totalProtein / todaysGoals.proteinGoal) * 100 : 0;

  // Determine colors
  const getColor = (percent) => {
    if (percent < 90) return '#FF6B35'; // Under goal - orange/red
    if (percent >= 90 && percent <= 110) return '#4ECDC4'; // At goal - teal
    return '#FFB84D'; // Over goal - yellow
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (dateString === today.toISOString().split('T')[0]) {
      return 'Today';
    } else if (dateString === yesterday.toISOString().split('T')[0]) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading nutrition data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="nutrition-tracker">
        <div className="nutrition-header">
          <button className="back-button" onClick={() => navigate('/')}>
            ← Back
          </button>
          <h1 className="page-title">🍽️ Nutrition Tracker</h1>
        </div>

        {/* Date Selector */}
        <div className="date-selector">
          <button className="date-nav-btn" onClick={() => handleDateChange(-1)}>
            ←
          </button>
          <div className="date-display">
            <div className="date-label">{formatDate(selectedDate)}</div>
            <div className="date-value">{new Date(selectedDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
          </div>
          <button className="date-nav-btn" onClick={() => handleDateChange(1)}>
            →
          </button>
        </div>

        {/* Calories Progress Bar */}
        <div className="nutrition-bar-card">
          <div className="bar-header">
            <div className="bar-label">
              <span className="bar-icon">🔥</span>
              <span className="bar-title">Calories</span>
            </div>
            <div className="bar-actions">
              {savedFoodItems.length > 0 && (
                <button
                  className="saved-foods-quick-btn"
                  onClick={() => {
                    setShowCalorieInput(true);
                    setShowSavedItems(true);
                  }}
                  title={`${savedFoodItems.length} saved items`}
                >
                  📋 Saved
                </button>
              )}
              <button
                className="add-btn"
                onClick={() => setShowCalorieInput(!showCalorieInput)}
              >
                +
              </button>
            </div>
          </div>

          {showCalorieInput && (
            <>
              {/* Quick Action Buttons - Primary Options */}
              <div className="quick-action-buttons">
                <button
                  className="quick-action-btn barcode-btn"
                  onClick={() => setShowBarcodeScanner(true)}
                  disabled={editingEntry}
                >
                  📷 Scan Barcode
                </button>
                <button
                  className="quick-action-btn search-btn"
                  onClick={() => setShowFoodSearch(true)}
                  disabled={editingEntry}
                >
                  🔍 Search Foods
                </button>
              </div>

              {/* Saved Items Button */}
              <div className="saved-items-btn-container">
                <button
                  className="saved-items-btn"
                  onClick={() => setShowSavedItems(!showSavedItems)}
                >
                  📋 {showSavedItems ? 'Hide' : 'Show'} Saved Items ({savedFoodItems.length})
                </button>
              </div>

              {showSavedItems && savedFoodItems.length > 0 && (
                <div className="saved-items-list">
                  {savedFoodItems.map((item) => {
                    const servingInfo = item.serving_unit
                      ? `${item.serving_qty || 1} ${item.serving_unit}`
                      : item.serving_size || '';

                    return (
                      <div key={item.id} className="saved-item">
                        {item.photo_url && (
                          <img src={item.photo_url} alt={item.food_name} className="saved-item-photo" />
                        )}
                        <div className="saved-item-content" onClick={() => handleSelectSavedItem(item)}>
                          <div className="saved-item-header">
                            <span className="saved-item-name">{item.food_name || item.name}</span>
                            {item.brand_name && (
                              <span className="saved-item-brand">{item.brand_name}</span>
                            )}
                          </div>
                          <span className="saved-item-values">
                            {item.calories > 0 && `${item.calories} cal`}
                            {item.calories > 0 && item.protein > 0 && ' • '}
                            {item.protein > 0 && `${item.protein}g protein`}
                            {servingInfo && ` (${servingInfo})`}
                          </span>
                        </div>
                        <button
                          className="saved-item-delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSavedItem(item.id);
                          }}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* AI Estimator - Collapsible, Last Resort Option */}
              <div className="ai-estimator-container">
                <button
                  className="ai-estimator-toggle"
                  onClick={() => setShowAiEstimator(!showAiEstimator)}
                >
                  🤖 AI Estimator {showAiEstimator ? '▼' : '▶'}
                </button>

                {showAiEstimator && (
                  <>
                    <div className="ai-meal-estimation">
                      <div className="ai-meal-input-container">
                        <input
                          type="text"
                          placeholder="Describe your meal (e.g., 'large burger with fries and a coke')"
                          value={aiMealDescription}
                          onChange={(e) => setAiMealDescription(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && !aiLoading) {
                              handleAiMealEstimate();
                            }
                          }}
                          className="ai-meal-input"
                          disabled={aiLoading || editingEntry}
                        />
                        <button
                          className="ai-estimate-btn"
                          onClick={handleAiMealEstimate}
                          disabled={aiLoading || !aiMealDescription.trim() || editingEntry}
                        >
                          {aiLoading ? '⏳ Analyzing...' : '✨ Estimate'}
                        </button>
                      </div>
                      {editingEntry && (
                        <div className="ai-disabled-note">AI estimator is disabled while editing an entry</div>
                      )}
                    </div>

                    {/* Complex Meal Items Breakdown */}
                    {complexMealItems.length > 0 && (
                      <div className="complex-meal-breakdown">
                        <div className="complex-meal-header">
                          <span className="complex-meal-icon">🍽️</span>
                          <span className="complex-meal-title">Meal Components</span>
                        </div>
                        <div className="complex-meal-items">
                          {complexMealItems.map((item, index) => (
                            <div key={index} className="complex-meal-item">
                              <div className="complex-item-header">
                                <span className="complex-item-name">{item.mealName}</span>
                                <button
                                  className="complex-item-remove"
                                  onClick={() => handleRemoveComplexItem(index)}
                                  title="Remove this item"
                                >
                                  ×
                                </button>
                              </div>
                              <div className="complex-item-info">
                                <span className="complex-item-serving">Serving: {item.servingSize}</span>
                                <span className="complex-item-nutrition">{item.calories} cal • {item.protein}g protein</span>
                              </div>
                              <div className="complex-item-servings">
                                <label>Number of Servings:</label>
                                <input
                                  type="number"
                                  step="0.5"
                                  min="0.5"
                                  value={item.servings}
                                  onChange={(e) => handleComplexMealServingsChange(index, e.target.value)}
                                  className="complex-servings-input"
                                />
                              </div>
                              {item.servings !== 1 && (
                                <div className="complex-item-total">
                                  Total: {Math.round(item.calories * item.servings)} cal • {Math.round(item.protein * item.servings)}g protein
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        <div className="complex-meal-actions">
                          <button
                            className="add-all-complex-btn"
                            onClick={handleAddAllComplexItems}
                          >
                            ✅ Add All Items
                          </button>
                          <button
                            className="cancel-complex-btn"
                            onClick={() => setComplexMealItems([])}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="quick-input-form">
                <input
                  type="text"
                  placeholder="Food name"
                  value={foodDescription}
                  onChange={(e) => setFoodDescription(e.target.value)}
                  className="quick-input-field"
                />
                <input
                  type="text"
                  placeholder="Serving size (e.g., 1 cup, 100g)"
                  value={servingSize}
                  onChange={(e) => setServingSize(e.target.value)}
                  className="quick-input-field"
                />
                <div className="per-serving-label">Per Serving:</div>
                <div className="quick-input">
                  <input
                    type="number"
                    placeholder="Calories"
                    value={calorieInput}
                    onChange={(e) => setCalorieInput(e.target.value)}
                    className="quick-input-field"
                    autoFocus
                  />
                  <input
                    type="number"
                    placeholder="Protein (g)"
                    value={proteinInput}
                    onChange={(e) => setProteinInput(e.target.value)}
                    className="quick-input-field"
                  />
                </div>
                <div className="servings-input-container">
                  <label className="servings-label">Number of Servings:</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0.5"
                    placeholder="1"
                    value={servings}
                    onChange={(e) => setServings(e.target.value)}
                    className="servings-input"
                  />
                </div>
                {calorieInput && proteinInput && servings && parseFloat(servings) !== 1 && (
                  <div className="totals-preview">
                    Total: {Math.round((parseInt(calorieInput) || 0) * parseFloat(servings))} cal • {Math.round((parseInt(proteinInput) || 0) * parseFloat(servings))}g protein
                  </div>
                )}
                {foodDescription && !editingEntry && (
                  <label className="save-to-common-label">
                    <input
                      type="checkbox"
                      checked={saveToCommon}
                      onChange={(e) => setSaveToCommon(e.target.checked)}
                      className="save-to-common-checkbox"
                    />
                    Save to Common Items
                  </label>
                )}
                <div className="quick-input-actions">
                  <button
                    className="quick-add-btn"
                    onClick={editingEntry ? handleUpdateEntry : handleAddEntry}
                  >
                    {editingEntry ? 'Update Entry' : 'Add Entry'}
                  </button>
                  <button
                    className="quick-cancel-btn"
                    onClick={() => {
                      setShowCalorieInput(false);
                      setCalorieInput('');
                      setProteinInput('');
                      setFoodDescription('');
                      setServingSize('');
                      setServings('1');
                      setSaveToCommon(false);
                      setCurrentFoodData(null);
                      setShowSavedItems(false);
                      setEditingEntry(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </>
          )}

          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{
                width: `${Math.min(caloriePercent, 100)}%`,
                backgroundColor: getColor(caloriePercent)
              }}
            />
            {caloriePercent > 100 && (
              <div
                className="progress-bar-overflow"
                style={{
                  width: `${Math.min(caloriePercent - 100, 100)}%`
                }}
              />
            )}
          </div>

          <div className="progress-text">
            <span className="progress-current" style={{ color: getColor(caloriePercent) }}>
              {totalCalories}
            </span>
            <span className="progress-separator"> / </span>
            <span className="progress-goal">{todaysGoals.calorieGoal} cal</span>
          </div>
        </div>

        {/* Protein Progress Bar */}
        <div className="nutrition-bar-card">
          <div className="bar-header">
            <div className="bar-label">
              <span className="bar-icon">💪</span>
              <span className="bar-title">Protein</span>
            </div>
          </div>

          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{
                width: `${Math.min(proteinPercent, 100)}%`,
                backgroundColor: getColor(proteinPercent)
              }}
            />
            {proteinPercent > 100 && (
              <div
                className="progress-bar-overflow"
                style={{
                  width: `${Math.min(proteinPercent - 100, 100)}%`
                }}
              />
            )}
          </div>

          <div className="progress-text">
            <span className="progress-current" style={{ color: getColor(proteinPercent) }}>
              {totalProtein}
            </span>
            <span className="progress-separator">g / </span>
            <span className="progress-goal">{todaysGoals.proteinGoal}g</span>
          </div>
        </div>

        {/* Entry Log */}
        {entries.length > 0 && (
          <div className="entries-card">
            <h3 className="card-title">📝 Today's Entries</h3>
            <div className="entries-list">
              {entries.map((entry, index) => (
                <div key={entry.id || index} className="entry-item">
                  <div className="entry-main-content">
                    {(entry.food_description || entry.meal_name) && (
                      <div className="entry-food-name">{entry.food_description || entry.meal_name}</div>
                    )}
                    <div className="entry-macros">
                      {entry.calories > 0 && (
                        <span className="entry-macro">🔥 {entry.calories} cal</span>
                      )}
                      {entry.protein > 0 && (
                        <span className="entry-macro">💪 {entry.protein}g protein</span>
                      )}
                    </div>
                  </div>
                  <div className="entry-actions">
                    <button
                      className="entry-edit-btn"
                      onClick={() => handleEditEntry(entry)}
                      title="Edit entry"
                    >
                      ✏️
                    </button>
                    <button
                      className="entry-delete-btn"
                      onClick={() => handleDeleteEntry(entry)}
                      title="Delete entry"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              className="log-new-item-btn"
              onClick={() => {
                setEditingEntry(null);
                setShowCalorieInput(true);
                setCalorieInput('');
                setProteinInput('');
                setFoodDescription('');
                setServingSize('');
                setServings('1');
                setSaveToCommon(false);
                setCurrentFoodData(null);
              }}
            >
              + Log New Item
            </button>
          </div>
        )}

        {/* Show quick add button if no entries */}
        {entries.length === 0 && !showCalorieInput && (
          <div className="empty-state">
            <p className="empty-message">No entries logged yet today</p>
            <button
              className="log-first-item-btn"
              onClick={() => setShowCalorieInput(true)}
            >
              + Log Your First Item
            </button>
          </div>
        )}
      </div>

      {/* Barcode Scanner Modal */}
      {showBarcodeScanner && (
        <BarcodeScanner
          onFoodFound={handleFoodFound}
          onClose={() => setShowBarcodeScanner(false)}
        />
      )}

      {/* Food Search Modal */}
      {showFoodSearch && (
        <FoodSearchAutocomplete
          onFoodSelected={handleFoodFound}
          onClose={() => setShowFoodSearch(false)}
        />
      )}
    </div>
  );
}

export default NutritionTracker;
